/**
 * Home — Day 5 live demo flow.
 *
 * Two modes, selectable via a segmented control at the top of the inputs:
 *
 *   Single match — upload/paste one resume + JD, get the full seven-signal
 *     breakdown + evidence drawer + chat copilot + autonomous agent + bias audit.
 *
 *   Bulk ranking — upload one JD + up to 20 resumes, watch them stream in
 *     as they score, get a sortable+filterable ranking table with expandable
 *     rows that show the full single-match drilldown for each candidate.
 *     CSV export for the ranked list.
 *
 * Both modes share the same engineMatch pipeline under the hood, so every
 * feature (evidence, chat, agent, bias audit) works identically regardless
 * of entry point.
 */

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Loader2, Play, RotateCcw, Trash2 } from "lucide-react";

import type { JobDescription, Match, Resume } from "@talent-align/engine";

import { BenchmarkBadge } from "@/components/BenchmarkBadge";
import {
  BulkDropzone,
  type BulkCandidateUpload,
} from "@/components/bulk/BulkDropzone";
import { BulkProgress } from "@/components/bulk/BulkProgress";
import {
  ModeSwitcher,
  type MatchMode,
} from "@/components/bulk/ModeSwitcher";
import { CardSkeleton } from "@/components/common/CardSkeleton";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { ApiKeyInput } from "@/components/settings/ApiKeyInput";
import { EvidenceList } from "@/components/results/EvidenceList";
import { ScoreBadge } from "@/components/results/ScoreBadge";
import { SubScoreBars } from "@/components/results/SubScoreBars";
import { SamplePicker } from "@/components/samples/SamplePicker";
import { FileDropzone } from "@/components/upload/FileDropzone";
import { useOpenRouter } from "@/hooks/useOpenRouter";

// Day 10: lazy-load the heavy result components so Recharts + jsPDF +
// react-markdown stay out of the main bundle. RankingTable is also
// lazy because it pulls in every result component transitively.
const AgentTimeline = lazy(() =>
  import("@/components/agent/AgentTimeline").then((m) => ({
    default: m.AgentTimeline,
  })),
);
const BiasAuditCard = lazy(() =>
  import("@/components/bias/BiasAuditCard").then((m) => ({
    default: m.BiasAuditCard,
  })),
);
const ChatPanel = lazy(() =>
  import("@/components/chat/ChatPanel").then((m) => ({
    default: m.ChatPanel,
  })),
);
const RankingTable = lazy(() =>
  import("@/components/bulk/RankingTable").then((m) => ({
    default: m.RankingTable,
  })),
);
const ComparisonView = lazy(() =>
  import("@/components/bulk/ComparisonView").then((m) => ({
    default: m.ComparisonView,
  })),
);
const InterviewQuestions = lazy(() =>
  import("@/components/tools/InterviewQuestions").then((m) => ({
    default: m.InterviewQuestions,
  })),
);
const OutreachDrafter = lazy(() =>
  import("@/components/tools/OutreachDrafter").then((m) => ({
    default: m.OutreachDrafter,
  })),
);
import {
  computeStats,
  runBulkMatch,
  type BulkProgressStats,
  type CandidateSlot,
} from "@/lib/bulk-match";
import { engineMatch } from "@/lib/match-engine";
import { SAMPLES, type Sample } from "@/samples";

type SingleStatus =
  | { kind: "idle" }
  | {
      kind: "loading";
      phase: "parsing" | "scoring" | "reranking" | "explaining";
    }
  | {
      kind: "ok";
      match: Match;
      resume: Resume;
      jd: JobDescription;
      latencyMs: number;
      retried: boolean;
      scoreLatencyMs: number;
      explainLatencyMs: number;
      rerankLatencyMs: number;
    }
  | { kind: "error"; message: string };

export default function Home() {
  const { complete, readSettings } = useOpenRouter();

  // Top-level mode
  const [mode, setMode] = useState<MatchMode>("single");

  // Shared JD (the JD is the same across both modes — reused when mode switches)
  const [jdText, setJdText] = useState("");

  // --- Single-match state ---
  const [resumeText, setResumeText] = useState("");
  const [activeSampleId, setActiveSampleId] = useState<string | undefined>(undefined);
  const [singleStatus, setSingleStatus] = useState<SingleStatus>({ kind: "idle" });

  // --- Bulk state ---
  const [candidates, setCandidates] = useState<BulkCandidateUpload[]>([]);
  const [bulkSlots, setBulkSlots] = useState<readonly CandidateSlot[]>([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkAborting, setBulkAborting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const bulkAbortRef = useRef<AbortController | null>(null);

  // Pick up a preloaded sample from the Samples page (via sessionStorage)
  useEffect(() => {
    const preloadId = sessionStorage.getItem("ta-preload-sample");
    if (preloadId) {
      sessionStorage.removeItem("ta-preload-sample");
      const sample = SAMPLES.find((s) => s.id === preloadId);
      if (sample) {
        setResumeText(sample.resumeText);
        setJdText(sample.jdText);
        setActiveSampleId(sample.id);
        setMode("single");
      }
    }
  }, []);

  // ---------------------------------------------------------------------
  // Single mode actions
  // ---------------------------------------------------------------------
  function loadSample(sample: Sample) {
    setResumeText(sample.resumeText);
    setJdText(sample.jdText);
    setActiveSampleId(sample.id);
    setSingleStatus({ kind: "idle" });
  }

  function resetSingle() {
    setResumeText("");
    setJdText("");
    setActiveSampleId(undefined);
    setSingleStatus({ kind: "idle" });
  }

  async function runSingleMatch() {
    if (!resumeText.trim() || !jdText.trim()) {
      setSingleStatus({
        kind: "error",
        message: "Drop a resume and a JD (or pick a sample).",
      });
      return;
    }
    const settings = readSettings();
    if (!settings.openRouterApiKey) {
      setSingleStatus({
        kind: "error",
        message: "Add your OpenRouter API key in the settings panel above.",
      });
      return;
    }

    setSingleStatus({ kind: "loading", phase: "parsing" });

    setTimeout(() => {
      setSingleStatus((s) =>
        s.kind === "loading" ? { kind: "loading", phase: "scoring" } : s,
      );
    }, 300);
    setTimeout(() => {
      setSingleStatus((s) =>
        s.kind === "loading" ? { kind: "loading", phase: "reranking" } : s,
      );
    }, 2500);
    setTimeout(() => {
      setSingleStatus((s) =>
        s.kind === "loading" ? { kind: "loading", phase: "explaining" } : s,
      );
    }, 4000);

    try {
      const result = await engineMatch(
        complete,
        resumeText,
        jdText,
        settings.openRouterModel ?? "meta-llama/llama-3.3-70b-instruct",
      );
      setSingleStatus({
        kind: "ok",
        match: result.match,
        resume: result.resume,
        jd: result.jd,
        latencyMs: result.latencyMs,
        retried: result.retried,
        scoreLatencyMs: result.scoreLatencyMs,
        explainLatencyMs: result.explainLatencyMs,
        rerankLatencyMs: result.rerankLatencyMs,
      });
    } catch (err) {
      setSingleStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }

  // ---------------------------------------------------------------------
  // Bulk mode actions
  // ---------------------------------------------------------------------
  function addCandidates(uploads: BulkCandidateUpload[]) {
    setCandidates((prev) => [...prev, ...uploads]);
    setBulkError(null);
    setBulkSlots([]);
  }

  function removeCandidate(id: string) {
    setCandidates((prev) => prev.filter((c) => c.id !== id));
    setBulkSlots([]);
  }

  function clearBulk() {
    setCandidates([]);
    setBulkSlots([]);
    setBulkError(null);
    setJdText("");
  }

  async function runBulk() {
    if (!jdText.trim()) {
      setBulkError("Paste or upload a job description first.");
      return;
    }
    if (candidates.length === 0) {
      setBulkError("Drop at least one resume before ranking.");
      return;
    }
    const settings = readSettings();
    if (!settings.openRouterApiKey) {
      setBulkError("Add your OpenRouter API key in the settings panel above.");
      return;
    }

    setBulkError(null);
    setBulkRunning(true);
    const abort = new AbortController();
    bulkAbortRef.current = abort;

    try {
      await runBulkMatch(
        complete,
        jdText,
        candidates.filter((c) => !c.error && c.rawText.trim().length > 0),
        {
          concurrency: 3,
          model: settings.openRouterModel ?? "meta-llama/llama-3.3-70b-instruct",
          signal: abort.signal,
          onUpdate: (slots) => {
            setBulkSlots(slots);
          },
        },
      );
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : "Bulk match failed");
    } finally {
      setBulkRunning(false);
      setBulkAborting(false);
      bulkAbortRef.current = null;
    }
  }

  function abortBulk() {
    if (!bulkAbortRef.current) return;
    setBulkAborting(true);
    bulkAbortRef.current.abort();
  }

  const bulkStats: BulkProgressStats =
    bulkSlots.length > 0 ? computeStats(bulkSlots.slice()) : computeStats([]);
  const bulkComplete =
    !bulkRunning &&
    bulkSlots.length > 0 &&
    bulkStats.completedCount === bulkStats.total;

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-24">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest bg-blue-500/10 border border-blue-500/30 text-blue-300 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shadow-[0_0_8px_#60a5fa] animate-pulse" />
          AI-powered resume matching · Seven-signal scoring engine
        </span>
        <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight ta-gradient-text mb-4">
          AI Resume Matching,
          <br />
          Grounded in Evidence.
        </h1>
        <p className="max-w-2xl mx-auto text-base sm:text-lg text-slate-300 leading-relaxed mb-6">
          Seven-signal ML scoring with a streaming AI copilot, bias audits, and
          bulk ranking across entire candidate pools. All in your browser.
        </p>
        <BenchmarkBadge />
      </div>

      <div className="mb-6">
        <ApiKeyInput />
      </div>

      {/* Mode switcher */}
      <div className="flex items-center justify-center mb-6">
        <ModeSwitcher mode={mode} onChange={setMode} />
      </div>

      {mode === "single" ? (
        <SingleMode
          resumeText={resumeText}
          setResumeText={setResumeText}
          jdText={jdText}
          setJdText={setJdText}
          status={singleStatus}
          activeSampleId={activeSampleId}
          loadSample={loadSample}
          reset={resetSingle}
          runMatch={runSingleMatch}
          setActiveSampleId={setActiveSampleId}
          setStatus={setSingleStatus}
        />
      ) : (
        <BulkMode
          jdText={jdText}
          setJdText={setJdText}
          candidates={candidates}
          addCandidates={addCandidates}
          removeCandidate={removeCandidate}
          clearBulk={clearBulk}
          bulkRunning={bulkRunning}
          bulkAborting={bulkAborting}
          bulkSlots={bulkSlots}
          bulkStats={bulkStats}
          bulkComplete={bulkComplete}
          bulkError={bulkError}
          runBulk={runBulk}
          abortBulk={abortBulk}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Single-match view
// ---------------------------------------------------------------------------
interface SingleModeProps {
  resumeText: string;
  setResumeText: (v: string) => void;
  jdText: string;
  setJdText: (v: string) => void;
  status: SingleStatus;
  activeSampleId: string | undefined;
  loadSample: (sample: Sample) => void;
  reset: () => void;
  runMatch: () => void;
  setActiveSampleId: (v: string | undefined) => void;
  setStatus: (s: SingleStatus) => void;
}

function SingleMode({
  resumeText,
  setResumeText,
  jdText,
  setJdText,
  status,
  activeSampleId,
  loadSample,
  reset,
  runMatch,
  setActiveSampleId,
  setStatus,
}: SingleModeProps) {
  return (
    <>
      <div className="mb-6">
        <SamplePicker onPick={loadSample} activeId={activeSampleId} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="ta-card p-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Resume
            </label>
            <span className="text-xs text-slate-600">
              {resumeText.length.toLocaleString()} chars
            </span>
          </div>
          <div className="mb-3">
            <FileDropzone
              label="resume"
              accent="blue"
              onParsed={(r) => {
                setResumeText(r.text);
                setActiveSampleId(undefined);
                setStatus({ kind: "idle" });
              }}
            />
          </div>
          <textarea
            value={resumeText}
            onChange={(e) => setResumeText(e.target.value)}
            placeholder="...or paste the resume text here."
            rows={12}
            className="w-full rounded-lg bg-slate-900/70 border border-slate-800/60 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 font-mono resize-none"
          />
        </div>
        <div className="ta-card p-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Job description
            </label>
            <span className="text-xs text-slate-600">
              {jdText.length.toLocaleString()} chars
            </span>
          </div>
          <div className="mb-3">
            <FileDropzone
              label="job description"
              accent="purple"
              onParsed={(r) => {
                setJdText(r.text);
                setActiveSampleId(undefined);
                setStatus({ kind: "idle" });
              }}
            />
          </div>
          <textarea
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            placeholder="...or paste the JD here."
            rows={12}
            className="w-full rounded-lg bg-slate-900/70 border border-slate-800/60 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 font-mono resize-none"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center mb-10">
        <button
          type="button"
          onClick={runMatch}
          disabled={status.kind === "loading"}
          className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 disabled:opacity-60 disabled:cursor-wait transition-all flex items-center gap-2"
        >
          {status.kind === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {status.phase === "parsing" && "Parsing..."}
              {status.phase === "scoring" && "Running 7 signals..."}
              {status.phase === "reranking" && "Cross-encoder rerank..."}
              {status.phase === "explaining" && "Extracting evidence..."}
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run match
            </>
          )}
        </button>
        <button
          type="button"
          onClick={reset}
          className="px-5 py-3 rounded-lg bg-transparent border border-slate-800/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 transition flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      {status.kind === "error" && (
        <div className="ta-card p-6 border-rose-500/30 bg-rose-500/5">
          <div className="text-sm text-rose-300">{status.message}</div>
        </div>
      )}

      {status.kind === "ok" && (
        <div className="space-y-4">
          <ScoreBadge
            score={status.match.overallScore}
            verdict={status.match.verdict}
            crossEncoderScore={status.match.crossEncoderScore}
            biencoderScore={status.match.biencoderScore}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <SubScoreBars subScores={status.match.subScores} />
            <EvidenceList
              evidence={status.match.evidence}
              rationale={status.match.rationale}
            />
          </div>
          <ErrorBoundary label="Bias audit">
            <Suspense fallback={<CardSkeleton title="Bias audit" lines={3} />}>
              <BiasAuditCard
                match={status.match}
                resumeText={resumeText}
                jdText={jdText}
                candidateLabel={status.resume.displayName ?? undefined}
              />
            </Suspense>
          </ErrorBoundary>
          <ErrorBoundary label="Chat copilot">
            <Suspense fallback={<CardSkeleton title="Ask TalentAlign AI" lines={3} />}>
              <ChatPanel
                match={status.match}
                resume={status.resume}
                jd={status.jd}
              />
            </Suspense>
          </ErrorBoundary>
          <ErrorBoundary label="Autonomous analysis">
            <Suspense
              fallback={<CardSkeleton title="Autonomous analysis" lines={4} />}
            >
              <AgentTimeline
                match={status.match}
                resume={status.resume}
                jd={status.jd}
              />
            </Suspense>
          </ErrorBoundary>
          <div className="grid md:grid-cols-2 gap-4">
            <ErrorBoundary label="Interview questions">
              <Suspense fallback={<CardSkeleton title="Interview questions" lines={3} />}>
                <InterviewQuestions
                  match={status.match}
                  resume={status.resume}
                  jd={status.jd}
                />
              </Suspense>
            </ErrorBoundary>
            <ErrorBoundary label="Outreach email">
              <Suspense fallback={<CardSkeleton title="Outreach email" lines={3} />}>
                <OutreachDrafter
                  match={status.match}
                  resume={status.resume}
                  jd={status.jd}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
          <div className="text-xs text-slate-500 text-center space-y-1">
            <div>
              Matched with{" "}
              <code className="text-slate-400">{status.match.modelUsed}</code>
            </div>
            <div>
              Total {status.latencyMs}ms · Engine {status.scoreLatencyMs}ms ·
              {status.rerankLatencyMs > 0 && (
                <> Rerank {status.rerankLatencyMs}ms ·</>
              )}{" "}
              Explain {status.explainLatencyMs}ms
              {status.retried && (
                <span className="text-amber-500"> · retried after JSON error</span>
              )}
            </div>
          </div>
        </div>
      )}

      {status.kind === "idle" && (
        <div className="ta-card p-6 text-center border-slate-800/60">
          <div className="text-sm text-slate-400">
            Pick a sample above, drop a PDF/DOCX, or paste text, then hit{" "}
            <span className="text-slate-200 font-medium">Run match</span>.
          </div>
          <div className="text-xs text-slate-600 mt-3">
            First match pre-loads the MiniLM embedding model (~25MB, cached
            forever after). Subsequent matches are instant.
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bulk view
// ---------------------------------------------------------------------------
interface BulkModeProps {
  jdText: string;
  setJdText: (v: string) => void;
  candidates: BulkCandidateUpload[];
  addCandidates: (uploads: BulkCandidateUpload[]) => void;
  removeCandidate: (id: string) => void;
  clearBulk: () => void;
  bulkRunning: boolean;
  bulkAborting: boolean;
  bulkSlots: readonly CandidateSlot[];
  bulkStats: BulkProgressStats;
  bulkComplete: boolean;
  bulkError: string | null;
  runBulk: () => void;
  abortBulk: () => void;
}

function BulkMode({
  jdText,
  setJdText,
  candidates,
  addCandidates,
  removeCandidate,
  clearBulk,
  bulkRunning,
  bulkAborting,
  bulkSlots,
  bulkStats,
  bulkComplete,
  bulkError,
  runBulk,
  abortBulk,
}: BulkModeProps) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* JD side */}
        <div className="ta-card p-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Job description (applies to all)
            </label>
            <span className="text-xs text-slate-600">
              {jdText.length.toLocaleString()} chars
            </span>
          </div>
          <div className="mb-3">
            <FileDropzone
              label="job description"
              accent="purple"
              onParsed={(r) => setJdText(r.text)}
            />
          </div>
          <textarea
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            placeholder="...or paste the JD here."
            rows={12}
            className="w-full rounded-lg bg-slate-900/70 border border-slate-800/60 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500/30 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 font-mono resize-none"
          />
        </div>

        {/* Candidates side */}
        <div className="ta-card p-5">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Candidates
            </label>
            <span className="text-xs text-slate-600">
              {candidates.length} / 20
            </span>
          </div>
          <div className="mb-3">
            <BulkDropzone
              onUpload={addCandidates}
              existingCount={candidates.length}
              maxCandidates={20}
            />
          </div>
          <ul className="space-y-1 max-h-[280px] overflow-y-auto pr-1">
            {candidates.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-2 text-xs text-slate-300 rounded bg-slate-900/40 border border-slate-800/60 px-2 py-1.5"
              >
                <span className="truncate flex-1" title={c.sourceName}>
                  {c.sourceName}
                </span>
                {c.error ? (
                  <span className="text-rose-400 text-[10px]">{c.error}</span>
                ) : (
                  <span className="text-slate-600 text-[10px]">
                    {c.rawText.length.toLocaleString()} chars
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeCandidate(c.id)}
                  className="text-slate-600 hover:text-rose-400 transition"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
            {candidates.length === 0 && (
              <li className="text-xs text-slate-600 italic text-center py-4">
                No candidates added yet.
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center mb-8">
        <button
          type="button"
          onClick={runBulk}
          disabled={bulkRunning || candidates.length === 0 || !jdText.trim()}
          className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center gap-2"
        >
          {bulkRunning ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Ranking...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Rank {candidates.length || ""} candidate
              {candidates.length === 1 ? "" : "s"}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={clearBulk}
          disabled={bulkRunning}
          className="px-5 py-3 rounded-lg bg-transparent border border-slate-800/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 transition flex items-center gap-2 disabled:opacity-60"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      {bulkError && (
        <div className="ta-card p-4 border-rose-500/30 bg-rose-500/5 mb-4">
          <div className="text-sm text-rose-300">{bulkError}</div>
        </div>
      )}

      {(bulkRunning || bulkSlots.length > 0) && (
        <div className="mb-6">
          <BulkProgress
            slots={bulkSlots}
            stats={bulkStats}
            onAbort={bulkRunning ? abortBulk : undefined}
            aborting={bulkAborting}
          />
        </div>
      )}

      {bulkComplete && (
        <>
          <ErrorBoundary label="Ranking table">
            <Suspense
              fallback={<CardSkeleton title="Ranking" lines={6} />}
            >
              <RankingTable slots={bulkSlots} jdText={jdText} />
            </Suspense>
          </ErrorBoundary>
          <div className="mt-4">
            <ErrorBoundary label="Comparison">
              <Suspense
                fallback={<CardSkeleton title="Comparison" lines={4} />}
              >
                <ComparisonView slots={bulkSlots} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </>
      )}

      {!bulkRunning && bulkSlots.length === 0 && candidates.length > 0 && (
        <div className="ta-card p-6 text-center border-slate-800/60">
          <div className="text-sm text-slate-400">
            Ready to rank {candidates.length} candidate
            {candidates.length === 1 ? "" : "s"}. Hit{" "}
            <span className="text-slate-200 font-medium">Rank</span> when you're
            ready.
          </div>
        </div>
      )}
    </>
  );
}
